import Service, { type Registry as Services, service } from '@ember/service';
import {
  type OmitEmberDataTypes,
  pushAndReturnPayload,
} from 'frontend-utils/utils/ember-data';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { RestrictionAction as RestrictionActionModel } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import type { RestrictionActionPostPayload } from 'lumen/utils/constants/restriction-actions';

export type RestrictionAction = OmitEmberDataTypes<RestrictionActionModel>;

export interface RestrictionOverridePayload {
  context: string;
  from_date: string;
  rate_group_id?: string | null;
  subscription_id?: number;
  type: string;
  value: number;
}

export default class RepositoriesRestrictionActionService extends Service {
  @service declare private ajax: AjaxService;
  @service declare private store: Services['store'];

  async add(
    restrictionAction:
      | RestrictionActionPostPayload
      | RestrictionActionPostPayload[],
    override?: RestrictionOverridePayload | RestrictionOverridePayload[],
  ): Promise<{ apiResponse: RestrictionAction[] | null; isSuccess: boolean }> {
    let apiResponse: RestrictionAction[] | null = null;

    /* DEEP DISCOUNT TODO: this will be cleaned up in a follow-up ticket (COA-3860) */
    const restrictionActions = Array.isArray(restrictionAction)
      ? restrictionAction
      : [restrictionAction];

    const requestUrl = formatUrl(
      `/${APIGATEWAY}/pricing-assistant/restrictions/recommendations/action`,
      // Callers always pass at least one config, so index 0 is present.
      { subscription_id: restrictionActions[0]!.subscription },
      this.ajax.host,
    );

    const data: {
      override?: RestrictionOverridePayload[];
      restriction_action_configs: RestrictionActionPostPayload[];
    } = {
      restriction_action_configs: restrictionActions,
    };

    if (override) {
      const overrideArray = Array.isArray(override) ? override : [override];
      if (overrideArray.length > 0) {
        data.override = overrideArray;
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const response = await this.ajax.post(requestUrl, {
      data,
      contentType: 'application/json',
    });

    // SMART DISTRIBUTION TODO: we currently use pushAndReturnPayload because we still use a manual AJAX call here, but we need the normalised record to work well with types
    // Future improvement could be to see if we can get rid of this manual AJAX request and handle things fully with Ember data.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const normalisedResponse = pushAndReturnPayload(this.store, response);
    apiResponse = normalisedResponse[
      'restriction-actions'
    ] as RestrictionAction[];

    return {
      isSuccess: true,
      apiResponse,
    };
  }
}

declare module '@ember/service' {
  interface Registry {
    'repositories/restriction-action': RepositoriesRestrictionActionService;
  }
}
